import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../providers/AuthContext";
import HeaderBar from "../components/HeaderBar";
import Sidebar from "../components/Sidebar";
import Breadcrumbs from "../components/Breadcrumbs";
import MultipleSelectDropdown from "../components/MultipleSelectDropdown";
import ReactApexChart from "react-apexcharts";
import { getAgreementValueSummary } from "../api/Agreement";
import toast from "react-hot-toast";

const AgreementValueSummaryPage = () => {
  const { getAccessToken } = useAuth();

  // Filter states
  const [selectedOfficeBranches, setSelectedOfficeBranches] = useState([]);
  const [selectedAgreements, setSelectedAgreements] = useState([]);

  // Data states
  const [summaryData, setSummaryData] = useState([]);
  const [officeBranches, setOfficeBranches] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);

  // Chart states
  const [chartSeries, setChartSeries] = useState([]);
  const [chartCategories, setChartCategories] = useState([]);

  const fetchSummaryData = useCallback(async () => {
    try {
      const accessToken = await getAccessToken();
      const data = await getAgreementValueSummary(null, accessToken);

      setSummaryData(data);

      // Extract unique office branches and agreements (preserving officeBranch on each agreement for cascading)
      const uniqueBranches = [...new Set(data.map(d => d.officeBranch).filter(Boolean))];
      const uniqueAgreementsMap = new Map();
      data.forEach(d => {
        if (!uniqueAgreementsMap.has(d.agreementPublicId)) {
          uniqueAgreementsMap.set(d.agreementPublicId, {
            label: d.agreementName,
            value: d.agreementPublicId,
            officeBranch: d.officeBranch,
          });
        }
      });
      const uniqueAgreements = Array.from(uniqueAgreementsMap.values());

      setOfficeBranches(uniqueBranches.map(b => ({ label: b, value: b })));
      setAgreements(uniqueAgreements);

      // Select all by default
      setSelectedOfficeBranches(uniqueBranches);
      setSelectedAgreements(uniqueAgreements.map(a => a.value));

      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch summary data:", error);
      toast.error("Failed to load agreement value summary");
      setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  // Cascade: Agreement options filtered by selected Office Branches
  const availableAgreements = useMemo(() => {
    if (selectedOfficeBranches.length === 0) return agreements;
    return agreements.filter(a => selectedOfficeBranches.includes(a.officeBranch));
  }, [agreements, selectedOfficeBranches]);

  // Clean up Agreement selections that are no longer available
  useEffect(() => {
    const availableValues = availableAgreements.map(a => a.value);
    setSelectedAgreements(prev => prev.filter(v => availableValues.includes(v)));
  }, [availableAgreements]);

  useEffect(() => {
    if (!loading && summaryData.length > 0) {
      prepareChartData();
    }
  }, [loading, summaryData, selectedOfficeBranches, selectedAgreements]);

  const prepareChartData = () => {
    // Filter data based on selections
    const filteredData = summaryData.filter(item => {
      const branchMatch = selectedOfficeBranches.length === 0 ||
        selectedOfficeBranches.includes(item.officeBranch);
      const agreementMatch = selectedAgreements.length === 0 ||
        selectedAgreements.includes(item.agreementPublicId);
      return branchMatch && agreementMatch;
    });

    if (filteredData.length === 0) {
      setChartSeries([]);
      setChartCategories([]);
      return;
    }

    // Group data by office branch and sum values
    const branchData = {};
    filteredData.forEach(item => {
      const branch = item.officeBranch || 'Unknown';
      if (!branchData[branch]) {
        branchData[branch] = {
          domainsTotalValue: 0,
          licensesTotalValue: 0,
          usersTotalValue: 0,
          addOnServicesTotalValue: 0
        };
      }
      branchData[branch].domainsTotalValue += item.domainsTotalValue;
      branchData[branch].licensesTotalValue += item.licensesTotalValue;
      branchData[branch].usersTotalValue += item.usersTotalValue;
      branchData[branch].addOnServicesTotalValue += item.addOnServicesTotalValue;
    });

    // Create categories (office branch names)
    const categories = Object.keys(branchData).sort();

    // Create series for each value category
    const series = [
      {
        name: 'Domains',
        data: categories.map(branch => branchData[branch].domainsTotalValue)
      },
      {
        name: 'Licenses',
        data: categories.map(branch => branchData[branch].licensesTotalValue)
      },
      {
        name: 'Users',
        data: categories.map(branch => branchData[branch].usersTotalValue)
      },
      {
        name: 'Add-on Services',
        data: categories.map(branch => branchData[branch].addOnServicesTotalValue)
      }
    ];

    setChartSeries(series);
    setChartCategories(categories);
  };

  const getFilteredData = () => {
    return summaryData.filter(item => {
      const branchMatch = selectedOfficeBranches.length === 0 ||
        selectedOfficeBranches.includes(item.officeBranch);
      const agreementMatch = selectedAgreements.length === 0 ||
        selectedAgreements.includes(item.agreementPublicId);
      return branchMatch && agreementMatch;
    });
  };

  const getTotalValue = () => {
    return getFilteredData().reduce((sum, item) => sum + item.grandTotal, 0);
  };

  const getOfficeBranchTotals = () => {
    const filteredData = getFilteredData();
    const branchTotals = {};

    filteredData.forEach(item => {
      const branch = item.officeBranch || 'Unknown';
      if (!branchTotals[branch]) {
        branchTotals[branch] = 0;
      }
      branchTotals[branch] += item.grandTotal;
    });

    return Object.entries(branchTotals)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([branch, total]) => ({ branch, total }));
  };

  const chartOptions = {
    chart: {
      type: 'bar',
      height: 400,
      stacked: true,
      toolbar: {
        show: true
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        endingShape: 'rounded'
      },
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    xaxis: {
      categories: chartCategories,
      title: {
        text: 'Office Branch'
      },
      labels: {
        rotate: -45,
        rotateAlways: chartCategories.length > 5,
        style: {
          fontSize: '10px'
        }
      }
    },
    yaxis: {
      title: {
        text: 'Value (SEK)'
      },
      labels: {
        formatter: (val) => val ? val.toLocaleString('sv-SE') : '0'
      }
    },
    fill: {
      opacity: 1
    },
    tooltip: {
      y: {
        formatter: (val) => val ? val.toLocaleString('sv-SE', { style: 'currency', currency: 'SEK' }) : '0 SEK'
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'left'
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className={`${loading ? "loading" : ""} page-content`}>
            <Breadcrumbs />

            <div className="flex flex-row items-center pb-4">
              <h2>Agreement Value Summary</h2>
            </div>

            {/* Filters Section */}
            <div className="rounded-md shadow-md border w-full p-4 mb-4 bg-white">
              <h3 className="text-lg font-semibold mb-4">Filters</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <MultipleSelectDropdown
                  label="Office Branch"
                  options={officeBranches}
                  selected={selectedOfficeBranches}
                  onChange={setSelectedOfficeBranches}
                  searchable={true}
                  placeholder="Select office branches..."
                />

                <MultipleSelectDropdown
                  label="Agreement"
                  options={availableAgreements}
                  selected={selectedAgreements}
                  onChange={setSelectedAgreements}
                  searchable={true}
                  placeholder="Select agreements..."
                />
              </div>
            </div>

            {/* Summary Cards */}
            {summaryData.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-4">
                <div className="rounded-md shadow-md border p-4 bg-white">
                  <h4 className="text-sm font-semibold text-gray-600 mb-2">
                    Total Value
                  </h4>
                  <p className="text-xl lg:text-2xl font-bold text-primary-700 break-words">
                    {getTotalValue().toLocaleString('sv-SE', {
                      style: 'currency',
                      currency: 'SEK'
                    })}
                  </p>
                </div>
                {getOfficeBranchTotals().map((branchData, index) => (
                  <div key={branchData.branch} className="rounded-md shadow-md border p-4 bg-white">
                    <h4 className="text-sm font-semibold text-gray-600 mb-2 truncate" title={branchData.branch}>
                      {branchData.branch}
                    </h4>
                    <p className="text-xl lg:text-2xl font-bold text-primary-700 break-words">
                      {branchData.total.toLocaleString('sv-SE', {
                        style: 'currency',
                        currency: 'SEK'
                      })}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Chart Section */}
            <div className="rounded-md shadow-md border w-full p-6 mb-4 bg-white">
              <h3 className="text-lg font-semibold mb-4">Value Breakdown by Office Branch</h3>

              {chartSeries.length > 0 ? (
                <ReactApexChart
                  options={chartOptions}
                  series={chartSeries}
                  type="bar"
                  height={400}
                />
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No data available for the selected filters
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AgreementValueSummaryPage;
